import React from 'react';
import { translateLabel } from './labelUtils';
import * as MuiIcons from '@mui/icons-material';
import { getPodLinkBehavior, isPrintOnDemandSection } from 'services/Utilities/podRoutingUtils';

const normalizeLocale = locale => (locale || '').toLowerCase().split('-')[0];

const getRequestedCultures = language => {
  const normalizedLanguage = normalizeLocale(language);

  if (normalizedLanguage === 'no') {
    return ['no', 'nb'];
  }

  if (normalizedLanguage === 'nb') {
    return ['nb', 'no'];
  }

  return normalizedLanguage ? [normalizedLanguage] : [];
};

const getMenuAttachedFiles = node => {
  if (Array.isArray(node?.menuAttachedFiles)) {
    return node.menuAttachedFiles;
  }

  return [];
};

const getMenuAttachedFileLanguageId = attachedFile => attachedFile?.language_Id || attachedFile?.language?.id || null;

const getMenuAttachedFileCulture = attachedFile => normalizeLocale(attachedFile?.language?.cultureString) || null;

const resolveLocalizedMenuAttachedFile = (node, language) => {
  const menuAttachedFiles = getMenuAttachedFiles(node);

  if (!menuAttachedFiles.length) {
    return null;
  }

  const requestedCultures = getRequestedCultures(language);
  const localizedMatch = requestedCultures.length
    ? menuAttachedFiles.find(attachedFile => requestedCultures.includes(getMenuAttachedFileCulture(attachedFile)))
    : null;
  const allLanguagesMatch = menuAttachedFiles.find(
    attachedFile => !getMenuAttachedFileLanguageId(attachedFile) && !getMenuAttachedFileCulture(attachedFile),
  );

  return localizedMatch || allLanguagesMatch || null;
};

/**
 * Maps an icon name string to a MUI icon component
 * @param {string} iconName - Name of the MUI icon (e.g., 'Home', 'Settings')
 * @returns {React.Element|null} MUI icon component or null if not found
 */
const getMuiIcon = iconName => {
  if (!iconName || typeof iconName !== 'string') {
    return null;
  }

  const IconComponent = MuiIcons[iconName];
  return IconComponent ? <IconComponent /> : null;
};

/**
 * Builds menu items based on current route and dealer portal menu tree data
 * @param {object} params - Parameters object
 * @param {string} params.pathname - Current location pathname
 * @param {function} params.t - Translation function
 * @param {array} params.dealerPortalMenuTreeData - Menu tree data from API
 * @param {string} params.language - Current language (i18n.language)
 * @param {string} params.currentSectionName - Current section name
 * @returns {array} Array of menu items
 */
export const buildMenuItems = ({ pathname, t, dealerPortalMenuTreeData, language, currentSectionName }) => {
  let horizontalItems = [];

  if (pathname.startsWith('/dealer/certification')) {
    horizontalItems.push({
      name: t('certNav.overview')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification`,
      order: 1,
    });
    horizontalItems.push({
      name: t('certNav.submissions')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification/submissions`,
      order: 2,
      dataTour: 'navCertification',
    });
    horizontalItems.push({
      name: t('certNav.userGuide')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification/user-manual`,
      order: 3,
      dataTour: 'navGuide',
    });
  } else {
    const treeNodes = Array.isArray(dealerPortalMenuTreeData) ? dealerPortalMenuTreeData : [];
    const sortedByOrder = nodes => [...nodes].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
    const getChildren = node => (Array.isArray(node?.children) ? node.children : []);
    const getMenuId = node => node?.id;
    const getMenuName = node => {
      const translated = translateLabel(node?.name, language);
      return translated || getMenuId(node) || '';
    };
    const getMobileMenuIcon = node => node?.mobileMenuIcon;
    const getResolvedMenuAttachedFiles = node => {
      const resolvedFile = resolveLocalizedMenuAttachedFile(node, language);
      return resolvedFile ? [resolvedFile] : [];
    };
    const getIsOnlyMenu = node => Boolean(node?.isOnlyMenu);
    const getMenuDescription = node => {
      const translated = translateLabel(node?.description, language);
      return translated || '';
    };
    const getMenuShortDescription = node => {
      const translated = translateLabel(node?.shortDescription, language);
      return translated || '';
    };
    const useLegacyPodLinkBehavior = isPrintOnDemandSection(currentSectionName);
    const buildMenuLinkData = menuId => {
      const internalLink = `/dealer/section/${currentSectionName}/menu/${menuId}`;

      if (!useLegacyPodLinkBehavior) {
        return {
          link: internalLink,
        };
      }

      const podLinkBehavior = getPodLinkBehavior(internalLink);

      return {
        link: podLinkBehavior.url,
        isExternalRoute: podLinkBehavior.isExternalRoute,
        openInNewTab: podLinkBehavior.openInNewTab,
      };
    };

    const mapChildNode = node => {
      const children = sortedByOrder(getChildren(node));
      const iconName = getMobileMenuIcon(node);
      const icon = iconName ? getMuiIcon(iconName) : undefined;
      const isOnlyMenu = getIsOnlyMenu(node);
      const linkData = !isOnlyMenu ? buildMenuLinkData(getMenuId(node)) : {};

      if (children.length === 0) {
        return {
          id: getMenuId(node),
          name: getMenuName(node),
          type: 'item',
          ...linkData,
          order: Number(node?.order ?? 0),
          menuAttachedFiles: getResolvedMenuAttachedFiles(node),
          description: getMenuDescription(node),
          shortDescription: getMenuShortDescription(node),
          ...(icon && { icon }),
        };
      }

      return {
        id: getMenuId(node),
        name: getMenuName(node),
        type: 'collapse',
        ...linkData,
        order: Number(node?.order ?? 0),
        menuAttachedFiles: getResolvedMenuAttachedFiles(node),
        description: getMenuDescription(node),
        shortDescription: getMenuShortDescription(node),
        children: children.map(mapChildNode),
        ...(icon && { icon }),
      };
    };

    const treeItems = sortedByOrder(treeNodes).map(node => {
      const children = sortedByOrder(getChildren(node));
      const iconName = getMobileMenuIcon(node);
      const icon = iconName ? getMuiIcon(iconName) : undefined;
      const isOnlyMenu = getIsOnlyMenu(node);
      const linkData = !isOnlyMenu ? buildMenuLinkData(getMenuId(node)) : {};

      if (children.length === 0) {
        return {
          id: getMenuId(node),
          name: getMenuName(node).toUpperCase(),
          type: 'item',
          ...linkData,
          order: Number(node?.order ?? 0),
          menuAttachedFiles: getResolvedMenuAttachedFiles(node),
          description: getMenuDescription(node),
          shortDescription: getMenuShortDescription(node),
          ...(icon && { icon }),
        };
      }

      return {
        id: getMenuId(node),
        name: getMenuName(node).toUpperCase(),
        type: 'collapse',
        ...linkData,
        order: Number(node?.order ?? 0),
        menuAttachedFiles: getResolvedMenuAttachedFiles(node),
        description: getMenuDescription(node),
        shortDescription: getMenuShortDescription(node),
        children: children.map(mapChildNode),
        ...(icon && { icon }),
      };
    });

    horizontalItems.push(...treeItems);
  }

  return horizontalItems;
};
